import React from 'react';
import { Link } from 'react-router-dom';
import PictureGallery from '../../components/PictureGallery';

const AboutUs = () => {
  return (
    <main id="about-us" className="w-full relative z-10">
      {/* Hero Section */}
      <section className="w-full bg-orange-50 relative z-0">
        <div className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="flex flex-col justify-center gap-6 text-center lg:text-left">
            <h1 className="font-serif text-3xl lg:text-4xl font-semibold text-coffee-dark leading-tight tracking-tight">Who We Are</h1>
            <p className="mt-2 text-sm text-gray-600">
              Funzo Coffee Limited was founded in 2019 and fully incorporated in Uganda. Our name, Funzo — from Mafunzo meaning “lessons” — reflects our journey of continuous learning and transformation in the coffee sector.
              We began as a green coffee trader and still trade it actively, exporting Uganda’s finest beans to international buyers. Through dedication, partnerships, and integrity, Funzo Coffee has built a solid reputation for consistency and reliability.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
              <Link to="/coffee" className="px-6 py-3 bg-coffee-primary text-white rounded-lg shadow-md transition-all duration-150 hover:bg-coffee-dark hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-coffee-accent">Explore Our Coffee</Link>
              <a href="#milestones" className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg text-coffee-primary transition-colors duration-200 hover:text-coffee-secondary hover:border-coffee-secondary focus:outline-none focus:ring-2 focus:ring-coffee-accent">
                <img src="https://api.iconify.design/lucide-clock.svg?color=%235C3A21" alt="Timeline Icon" className="w-5 h-5"/>
                See Milestones
              </a>
            </div>
          </div>
          <div className="relative rounded-xl overflow-hidden shadow-xl">
            <img src="/images/buildinga.png?query=%22coffee%20roastery%22&w=1200&h=800" alt="Roastery at Funzo Coffee" className="w-full h-96 object-cover"/>
          </div>
        </div>
      </section>

      {/* Why "Funzo"? Section */}
      <section className="w-full bg-white border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div className="relative rounded-xl overflow-hidden shadow-xl">
            <img src="https://app.grapesjs.com/api/assets/random-image?query=%22uganda%20coffee%20farm%22&w=1000&h=700" alt="Ugandan coffee farm" className="w-full h-96 object-cover"/>
          </div>
          <div className="flex flex-col gap-5">
            <h2 className="font-serif text-4xl text-coffee-dark">Why “Funzo”?</h2>
            <p className="mt-2 text-sm text-gray-600">
              Our name is born from “Mafunzo,” the Swahili word for lessons. It is a daily reminder of the learning embedded in every stage of coffee—soil, seed, harvest, and cup. We embrace the lessons from farmers, roasters, baristas, and customers to continually elevate Ugandan coffee.
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Funzo Coffee is a conversation between heritage and innovation. Our roots stretch across Uganda’s coffee heartlands, where altitude, climate, and patient care shape complex flavors—and our mission turns those lessons into value that resonates globally.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-2">
              <div className="inline-flex items-center gap-2">
                <img src="https://api.iconify.design/lucide-book-open.svg?color=%235C3A21" alt="Book Icon" className="w-5 h-5"/>
                <span className="text-base text-gray-700">Learning-led approach</span>
              </div>
              <div className="inline-flex items-center gap-2">
                <img src="https://api.iconify.design/lucide-leaf.svg?color=%235C3A21" alt="Leaf Icon" className="w-5 h-5"/>
                <span className="text-base text-gray-700">Respect for origin</span>
              </div>
              <div className="inline-flex items-center gap-2">
                <img src="https://api.iconify.design/lucide-globe.svg?color=%235C3A21" alt="Globe Icon" className="w-5 h-5"/>
                <span className="text-base text-gray-700">Global value chain impact</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Transition to Value Addition Section */}
      <section className="w-full bg-orange-50 border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="flex flex-col gap-5">
            <h2 className="font-serif text-4xl text-coffee-dark">Our Transition to Value Addition</h2>
            <p className="mt-2 text-sm text-gray-600">
              Our next chapter is driven by a bold Value Addition Agenda, anchored in our Business Expansion Plan. This plan involves setting up a modern processing facility with a roasting plant to scale the production of roast and ground coffee, and to later venture into instant coffee production.
              To achieve this, Funzo Coffee partnered with <a className="font-serif text-sm text-blue-500" href='https://tuccgroup.com'>TUCC Group</a> on the Uganda Coffee Value Addition Project — a flagship initiative that will transform Uganda’s coffee industry and increase local value retention.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4">
              <li className="p-4 bg-white rounded-xl shadow-sm transition-all duration-150 hover:bg-gray-50 focus-within:outline-none focus-within:ring-2 focus-within:ring-coffee-accent">
                <div className="flex items-start gap-3">
                  <img src="https://api.iconify.design/lucide-sprout.svg?color=%235C3A21" alt="Sprout Icon" className="w-6 h-6 mt-1"/>
                  <div>
                    <h3 className="text-xl font-semibold text-coffee-dark">Selective Sourcing</h3>
                    <p className="mt-2 text-sm text-gray-600">Partnering with growers across the Rwenzori, Bugisu, and Mt. Elgon regions for consistent excellence.</p>
                  </div>
                </div>
              </li>
              <li className="p-4 bg-white rounded-xl shadow-sm transition-all duration-150 hover:bg-gray-50 focus-within:outline-none focus-within:ring-2 focus-within:ring-coffee-accent">
                <div className="flex items-start gap-3">
                  <img src="https://api.iconify.design/lucide-flame.svg?color=%235C3A21" alt="Roast Icon" className="w-6 h-6 mt-1"/>
                  <div>
                    <h3 className="text-xl font-semibold text-coffee-dark">Roasting Mastery</h3>
                    <p className="mt-2 text-sm text-gray-600">Profiles that honor terroir, from bright washed Arabicas to deeply sweet naturals.</p>
                  </div>
                </div>
              </li>
              <li className="p-4 bg-white rounded-xl shadow-sm transition-all duration-150 hover:bg-gray-50 focus-within:outline-none focus-within:ring-2 focus-within:ring-coffee-accent">
                <div className="flex items-start gap-3">
                  <img src="https://api.iconify.design/lucide-beaker.svg?color=%235C3A21" alt="Quality Icon" className="w-6 h-6 mt-1"/>
                  <div>
                    <h3 className="text-xl font-semibold text-coffee-dark">Quality & Traceability</h3>
                    <p className="mt-2 text-sm text-gray-600">Cup score transparency and farm-level traceability guide every batch we produce.</p>
                  </div>
                </div>
              </li>
              <li className="p-4 bg-white rounded-xl shadow-sm transition-all duration-150 hover:bg-gray-50 focus-within:outline-none focus-within:ring-2 focus-within:ring-coffee-accent">
                <div className="flex items-start gap-3">
                  <img src="https://api.iconify.design/lucide-handshake.svg?color=%235C3A21" alt="Partnership Icon" className="w-6 h-6 mt-1"/>
                  <div>
                    <h3 className="text-xl font-semibold text-coffee-dark">Inclusive Partnerships</h3>
                    <p className="mt-2 text-sm text-gray-600">Strengthening farmer livelihoods with training and market access, especially for youth and women.</p>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <div className="grid grid-cols-1 gap-6"> 
            <div className="rounded-xl overflow-hidden shadow-xl">
              <img src="/images/building.jpeg?query=%22green%20coffee%20beans%22&w=1000&h=700" alt="Trading green coffee beans" className="w-full h-72 object-cover"/>
            </div>
            <div className="rounded-xl overflow-hidden shadow-xl">
              <img src="/images/p.jpg?query=%22coffee%20roasting%20process%22&w=1000&h=700" alt="Coffee roasting process" className="w-full h-72 object-cover"/>
            </div>
            <div className="rounded-xl overflow-hidden shadow-xl">
              <img src="/images/ladystory.jpg?query=%22coffee%20quality%20control%22&w=1000&h=700" alt="Quality control and cupping" loading="lazy" className="w-full h-82 object-cover"/>
            </div>
          </div>
        </div>
      </section>

      {/* Our Global Presence Section */}
      <section id="milestones" className="w-full bg-white/70 backdrop-blur-sm border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8">
            <h2 className="font-serif text-4xl text-coffee-dark">Our Global Presence</h2>
            <div className="flex items-center gap-2 mt-4 sm:mt-0">
              <span className="inline-block w-3 h-3 rounded-full bg-coffee-primary"></span>
              <span className="text-sm text-gray-700">Key achievements</span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="border border-gray-200 p-5 bg-white transition-colors duration-150 hover:bg-gray-50">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-sm font-semibold text-coffee-primary">2018</span>
                <img src="https://api.iconify.design/lucide-flag.svg?color=%235C3A21" alt="Flag Icon" className="w-5 h-5"/>
              </div>
              <h3 className="text-xl text-coffee-dark">Founded as Green Coffee Traders</h3>
              <p className="mt-2 text-sm text-gray-600">Built relationships with smallholder farmers and exporters, focusing on quality and fair trading practices.</p>
            </div>
            <div className="border border-gray-200 p-5 bg-white transition-colors duration-150 hover:bg-gray-50">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-sm font-semibold text-coffee-primary">2020</span>
                <img src="https://api.iconify.design/lucide-flame.svg?color=%235C3A21" alt="Flame Icon" className="w-5 h-5"/>
              </div>
              <h3 className="text-xl text-coffee-dark">Roastery Commissioned</h3>
              <p className="mt-2 text-sm text-gray-600">Launched artisanal roasting with traceable profiles to showcase Uganda’s distinct terroirs.</p>
            </div>
            <div className="border border-gray-200 p-5 bg-white transition-colors duration-150 hover:bg-gray-50">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-sm font-semibold text-coffee-primary">2022</span>
                <img src="https://api.iconify.design/lucide-award.svg?color=%235C3A21" alt="Award Icon" className="w-5 h-5"/>
              </div>
              <h3 className="text-xl text-coffee-dark">Quality Recognition</h3>
              <p className="mt-2 text-sm text-gray-600">Recognized by regional partners for consistency, traceability, and responsible sourcing.</p>
            </div>
            <div className="border border-gray-200 p-5 bg-white transition-colors duration-150 hover:bg-gray-50">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-sm font-semibold text-coffee-primary">2024</span>
                <img src="https://api.iconify.design/lucide-globe.svg?color=%235C3A21" alt="Globe Icon" className="w-5 h-5"/>
              </div>
              <h3 className="text-xl text-coffee-dark">Global Value Chain Impact</h3>
              <p className="mt-2 text-sm text-gray-600">Expanded value-added offerings, strengthening Uganda’s voice in specialty markets worldwide.</p>
            </div>
          </div>
        </div>
      </section>

     

      {/* Our Mission Section */}
      <section className="w-full bg-white/70 backdrop-blur-sm border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="flex flex-col gap-5">
            <h2 className="font-serif text-4xl text-coffee-dark">Our Mission</h2>
            <p className="mt-2 text-sm text-gray-600">To redefine Uganda’s position in the global coffee value chain by transforming lessons into lasting value—through quality, transparency, and community-centered innovation.</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
              <li className="p-4 bg-white rounded-xl shadow-sm border border-gray-200 transition-colors duration-150 hover:bg-gray-50">
                <div className="flex items-start gap-3">
                  <img src="https://api.iconify.design/lucide-compass.svg?color=%235C3A21" alt="Compass Icon" className="w-6 h-6 mt-1"/>
                  <div>
                    <h3 className="text-lg font-semibold text-coffee-dark">Quality Leadership</h3>
                    <p className="mt-2 text-sm text-gray-600">Elevate cup scores through science, craft, and traceability.</p>
                  </div>
                </div>
              </li>
              <li className="p-4 bg-white rounded-xl shadow-sm border border-gray-200 transition-colors duration-150 hover:bg-gray-50">
                <div className="flex items-start gap-3">
                  <img src="https://api.iconify.design/lucide-users.svg?color=%235C3A21" alt="Users Icon" className="w-6 h-6 mt-1"/>
                  <div>
                    <h3 className="text-lg font-semibold text-coffee-dark">Community Value</h3>
                    <p className="mt-2 text-sm text-gray-600">Share value with farmers through training and fair partnerships.</p>
                  </div>
                </div>
              </li>
              <li className="p-4 bg-white rounded-xl shadow-sm border border-gray-200 transition-colors duration-150 hover:bg-gray-50">
                <div className="flex items-start gap-3">
                  <img src="https://api.iconify.design/lucide-rocket.svg?color=%235C3A21" alt="Rocket Icon" className="w-6 h-6 mt-1"/>
                  <div>
                    <h3 className="text-lg font-semibold text-coffee-dark">Global Reach</h3>
                    <p className="mt-2 text-sm text-gray-600">Build Uganda’s voice in specialty coffee markets worldwide.</p>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <div className="rounded-xl overflow-hidden shadow-xl">
            <img src="https://app.grapesjs.com/api/assets/random-image?query=%22coffee%20production%20uganda%22&w=1000&h=700" alt="Coffee production process in Uganda" loading="lazy" className="w-full h-96 object-cover"/>
          </div>
        </div>
      </section>

      {/* From Farm to Cup Section */}
      <section className="w-full bg-white/70 backdrop-blur-sm border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="mb-8">
            <h2 className="font-serif text-4xl text-coffee-dark">From Farm to Cup</h2>
            <p className="mt-2 text-sm text-gray-600">A visual journey through cultivation, processing, roasting, and brewing.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <img src="/images/mulching.jpg?query=%22coffee%20cherries%22&w=900&h=700" alt="Coffee cherries on branch" className="w-full h-56 object-cover"/>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-coffee-dark">Cultivation</h3>
                <p className="mt-2 text-sm text-gray-600">Careful farming practices yield healthy cherries and vibrant flavors.</p>
              </div>
            </div>
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <img src="https://app.grapesjs.com/api/assets/random-image?query=%22coffee%20washing%20station%22&w=900&h=700" alt="Coffee washing station" className="w-full h-56 object-cover"/>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-coffee-dark">Processing</h3>
                <p className="mt-2 text-sm text-gray-600">Washed and natural processes highlight regional characteristics.</p>
              </div>
            </div>
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <img src="/images/CoffeeRoasting.jpg?query=%22coffee%20roasting%20drum%22&w=900&h=700" alt="Coffee roasting drum" className="w-full h-56 object-cover"/>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-coffee-dark">Roasting</h3>
                <p className="mt-2 text-sm text-gray-600">Profile-driven roasting transforms green beans into complex flavor.</p>
              </div>
            </div>
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <img src="https://app.grapesjs.com/api/assets/random-image?query=%22pour%20over%20coffee%22&w=900&h=700" alt="Pour over coffee brewing" loading="lazy" className="w-full h-56 object-cover"/>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-coffee-dark">Brewing</h3>
                <p className="mt-2 text-sm text-gray-600">Brew methods that honor origin and unlock sweetness and clarity.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects & Partnerships Section */}
      <section className="w-full bg-orange-50 border-t border-gray-200 py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-4xl text-coffee-dark mb-4">The Uganda Coffee Value Addition Project</h2>
          <p className="mt-2 text-sm text-gray-600">
            In partnership with TUCC Group.
            This initiative will boost Uganda’s coffee value addition capacity, create employment opportunities, and position the country as a competitive player in the global coffee market.
            <a className="font-serif text-sm text-blue-500" href='https://tuccgroup.com/our-project'> More about this project</a>
          </p>
          <PictureGallery />
        </div>
      </section>

      <section className="w-full bg-orange-50 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-4xl text-coffee-dark mb-4"> PACEID Collaboration</h2>
          <p className="mt-2 text-sm text-gray-600">
            With the support of the Presidential Advisory Committee on Exports and Industrial Development (PACEID), Funzo Coffee is tapping into new global markets, including China, to expand Uganda’s coffee export footprint.
             <a className="text-blue-500" href='https://paceid.org'>More about PACEID...</a>
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-8 text-center">
        <Link to="/projects-partnerships" className="inline-flex items-center gap-2 px-6 py-3 bg-coffee-primary text-white rounded-lg shadow-md transition-all duration-150 hover:bg-coffee-dark hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-coffee-accent">
          Learn More About Our Projects →
        </Link>
      </div>
    </main>
  );
};

export default AboutUs;
